/**
 * Color-related semantic tokens for PrimeNG preset
 * Maps Figma semantic tokens to PrimeNG's color structure
 */

import * as a from '../tokens/aliases.js';
import * as p from '../tokens/primitives.js';
import { border, interactive, surface, text, type AliasSet, type SemanticTokens } from '../tokens/semantic.js';

export const colors = {
  // Text (from semantic.ts)
  text: {
    color: text.secondary,
    primary: text.primary,
    secondary: text.secondary,
    tertiary: text.tertiary,
    muted: text.muted,
    disabled: text.disabled,
    onColor: text.onColor,
    success: text.success,
    warning: text.warning,
    danger: text.danger,
    link: text.link,
    linkHover: text.linkHover,
  },

  // Surface (from semantic.ts)
  surface: {
    ground: surface.ground,
    canvas: surface.canvas,
    raised: surface.raised,
    modal: surface.modal,
    disabled: surface.disabled,
    dark: surface.dark,
  },

  // Border
  border: {
    default: border.default,
    color: border.default,
    strong: border.strong,
    divider: border.divider,
    primary: border.primary,
    secondary: border.secondary,
    success: border.success,
    warning: border.warning,
    danger: border.danger,
    focus: border.focus,
    disabled: border.disabled,
  },

  // Content
  content: {
    background: surface.ground,
    color: text.secondary,
  },

  // Semantic palette extensions (PrimeNG needs these for '{primary.color}' etc.)
  primary: {
    ...a.primary,
    color: interactive.primary.base,
    hover: {
      color: interactive.primary.hover,
    },
    active: {
      color: interactive.primary.pressed,
    },
    contrast: {
      color: text.onColor,
    },
  },
  secondary: { 
    ...a.secondary,
    color: interactive.secondary.base,
    hover: {
      color: interactive.secondary.hover,
    },
    active: {
      color: interactive.secondary.pressed,
    },
    contrast: {
      color: text.onColor,
    },
  },
  success: { 
    ...a.success,
    color: interactive.success.base,
    hover: {
      color: interactive.success.hover,
    },
    active: {
      color: interactive.success.pressed,
    },
    contrast: {
      color: text.onColor,
    },
  },
  warning: { 
    ...a.warning,
    color: interactive.warning.base,
    hover: {
      color: interactive.warning.hover,
    },
    active: {
      color: interactive.warning.pressed,
    },
    contrast: {
      color: text.onColor,
    },
  },
  danger: { 
    ...a.danger,
    color: interactive.danger.base,
    hover: {
      color: interactive.danger.hover,
    },
    active: {
      color: interactive.danger.pressed,
    },
    contrast: {
      color: text.onColor,
    },
  },

  // Neutral palette (for surface variants and utility classes)
  neutral: { ...p.neutral },

  // Sub-brand palettes (for Tailwind utility classes)
  lock: { ...a.lock },
  home: { ...a.home },
  guest: { ...a.guest },
};

export function createColors(s: SemanticTokens, al: AliasSet) {
  return {
    text: {
      color: s.text.secondary,
      primary: s.text.primary,
      secondary: s.text.secondary,
      tertiary: s.text.tertiary,
      muted: s.text.muted,
      disabled: s.text.disabled,
      onColor: s.text.onColor,
      success: s.text.success,
      warning: s.text.warning,
      danger: s.text.danger,
      link: s.text.link,
      linkHover: s.text.linkHover,
    },
    surface: {
      ground: s.surface.ground,
      canvas: s.surface.canvas,
      raised: s.surface.raised,
      modal: s.surface.modal,
      disabled: s.surface.disabled,
      dark: s.surface.dark,
    },
    border: {
      default: s.border.default,
      color: s.border.default,
      strong: s.border.strong,
      divider: s.border.divider,
      primary: s.border.primary,
      secondary: s.border.secondary,
      success: s.border.success,
      warning: s.border.warning,
      danger: s.border.danger,
      focus: s.border.focus,
      disabled: s.border.disabled,
    },
    content: {
      background: s.surface.ground,
      color: s.text.secondary,
    },
    primary: {
      ...al.primary,
      color: s.interactive.primary.base,
      hover: { color: s.interactive.primary.hover },
      active: { color: s.interactive.primary.pressed },
      contrast: { color: s.text.onColor },
    },
    secondary: {
      ...al.secondary,
      color: s.interactive.secondary.base,
      hover: { color: s.interactive.secondary.hover },
      active: { color: s.interactive.secondary.pressed },
      contrast: { color: s.text.onColor },
    },
    success: {
      ...al.success,
      color: s.interactive.success.base,
      hover: { color: s.interactive.success.hover },
      active: { color: s.interactive.success.pressed },
      contrast: { color: s.text.onColor },
    },
    warning: {
      ...al.warning,
      color: s.interactive.warning.base,
      hover: { color: s.interactive.warning.hover },
      active: { color: s.interactive.warning.pressed },
      contrast: { color: s.text.onColor },
    },
    danger: {
      ...al.danger,
      color: s.interactive.danger.base,
      hover: { color: s.interactive.danger.hover },
      active: { color: s.interactive.danger.pressed },
      contrast: { color: s.text.onColor },
    },
    neutral: { ...p.neutral },
    lock: { ...a.lock },
    home: { ...a.home },
    guest: { ...a.guest },
  };
}
